import * as React from "react";

export interface SelectedMailItem {
  messageId: string;
  itemId?: string;
  restId?: string;
  internetMessageId?: string;
  subject: string;
}

interface SelectedMailboxItem {
  itemId?: string;
  internetMessageId?: string;
  subject?: string;
}

function toSelectedMailItem(item: SelectedMailboxItem): SelectedMailItem | undefined {
  if (!item.itemId) return undefined;

  const restId =
    typeof Office.context.mailbox.convertToRestId === "function"
      ? Office.context.mailbox.convertToRestId(item.itemId, Office.MailboxEnums.RestVersion.v2_0)
      : undefined;

  return {
    messageId: restId || item.itemId,
    itemId: item.itemId,
    restId,
    internetMessageId: item.internetMessageId,
    subject: item.subject || "email",
  };
}

function isSelectedMailItem(item: SelectedMailItem | undefined): item is SelectedMailItem {
  return item !== undefined;
}

export function useSelectedMailItems(): SelectedMailItem[] {
  const [selectedItems, setSelectedItems] = React.useState<SelectedMailItem[]>([]);

  const refreshSelectedItems = React.useCallback(() => {
    const getSelectedItemsAsync = (Office.context.mailbox as any).getSelectedItemsAsync;
    if (typeof getSelectedItemsAsync !== "function") {
      setSelectedItems([]);
      return;
    }

    getSelectedItemsAsync((asyncResult: Office.AsyncResult<SelectedMailboxItem[]>) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        console.error(asyncResult.error.message);
        setSelectedItems([]);
        return;
      }

      setSelectedItems((asyncResult.value || []).map(toSelectedMailItem).filter(isSelectedMailItem));
    });
  }, []);

  React.useEffect(() => {
    refreshSelectedItems();

    const mailbox = Office.context.mailbox as any;
    if (typeof mailbox.addHandlerAsync !== "function") return undefined;

    mailbox.addHandlerAsync(
      Office.EventType.SelectedItemsChanged,
      refreshSelectedItems,
      (asyncResult: Office.AsyncResult<void>) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error(asyncResult.error.message);
        }
      }
    );

    return () => {
      if (typeof mailbox.removeHandlerAsync === "function") {
        mailbox.removeHandlerAsync(Office.EventType.SelectedItemsChanged, {
          handler: refreshSelectedItems,
        });
      }
    };
  }, [refreshSelectedItems]);

  return selectedItems;
}
